import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, DatabaseZap, Trash2, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { validateImageUrl } from "./seedData";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface AdminSeedButtonProps {
  userId: string;
  onComplete: () => void;
}

// Each "wave" calls auto-expand-media which pulls from Unsplash/Pexels/Pixabay,
// dedups by content hash, uploads to Storage, enriches multilingual tags.
const TARGET = 1000;
const QUERIES_PER_WAVE = 12;
const IMAGES_PER_QUERY = 6;
const PER_CATEGORY_CAP = 200;
const MAX_WAVES = 30;

export default function AdminSeedButton({ userId, onComplete }: AdminSeedButtonProps) {
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [purging, setPurging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const { toast } = useToast();

  const countAssets = async () => {
    const { count } = await supabase
      .from("media_assets")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    return count || 0;
  };

  const clearLibrary = async () => {
    setClearing(true);
    try {
      const { error } = await supabase
        .from("media_assets")
        .delete()
        .eq("user_id", userId);
      if (error) throw error;
      toast({ title: "Media library cleared" });
      onComplete();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setClearing(false);
    }
  };

  const purgebrokenRecords = useCallback(async () => {
    setPurging(true);
    setStatusText("Scanning URLs...");
    setProgress(0);
    try {
      let allAssets: { id: string; file_url: string }[] = [];
      let from = 0;
      const PAGE = 1000;
      while (true) {
        const { data, error } = await supabase
          .from("media_assets")
          .select("id, file_url")
          .eq("user_id", userId)
          .range(from, from + PAGE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        allAssets = allAssets.concat(data);
        if (data.length < PAGE) break;
        from += PAGE;
      }

      if (allAssets.length === 0) {
        toast({ title: "No media to check" });
        setPurging(false);
        setStatusText("");
        return;
      }

      const BATCH = 10;
      let broken = 0;
      let checked = 0;

      for (let i = 0; i < allAssets.length; i += BATCH) {
        const batch = allAssets.slice(i, i + BATCH);
        const results = await Promise.all(
          batch.map(async (asset) => {
            const valid = await validateImageUrl(asset.file_url, 6000);
            return { id: asset.id, valid };
          })
        );

        const brokenIds = results.filter((r) => !r.valid).map((r) => r.id);
        if (brokenIds.length > 0) {
          for (const id of brokenIds) {
            await supabase.from("media_assets").delete().eq("id", id);
          }
          broken += brokenIds.length;
        }

        checked += batch.length;
        setProgress(Math.round((checked / allAssets.length) * 100));
        setStatusText(`Checking: ${checked}/${allAssets.length}`);
      }

      toast({
        title: "Purge complete",
        description: `${broken} broken links removed out of ${allAssets.length} checked.`,
      });
      if (broken > 0) onComplete();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setPurging(false);
      setStatusText("");
      setProgress(0);
    }
  }, [userId, onComplete, toast]);

  const seed = async () => {
    setSeeding(true);
    setProgress(0);
    setStatusText("Preparing real-image harvest...");

    try {
      const initialCount = await countAssets();
      let totalImported = 0;
      let totalSkipped = 0;
      let totalFailed = 0;

      for (let wave = 0; wave < MAX_WAVES; wave++) {
        const currentCount = initialCount + totalImported;
        if (currentCount >= initialCount + TARGET) break;

        setStatusText(
          `Wave ${wave + 1}: harvesting from Unsplash / Pexels / Pixabay...`
        );

        const { data, error } = await supabase.functions.invoke("auto-expand-media", {
          body: {
            queries_per_run: QUERIES_PER_WAVE,
            images_per_query: IMAGES_PER_QUERY,
            per_category_cap: PER_CATEGORY_CAP,
          },
        });

        if (error) throw new Error(error.message || "Edge function failed");

        const imp = data?.imported || 0;
        const skp = data?.skipped || 0;
        const fld = data?.failed || 0;
        totalImported += imp;
        totalSkipped += skp;
        totalFailed += fld;

        const pct = Math.min(100, Math.round((totalImported / TARGET) * 100));
        setProgress(pct);
        setStatusText(
          `${totalImported}/${TARGET} imported · ${totalSkipped} skipped · ${totalFailed} failed`
        );

        // Stop if a full wave produced nothing (sources exhausted / capped)
        if (imp === 0 && skp === 0) break;
      }

      if (totalImported === 0) {
        toast({
          title: "No new images imported",
          description:
            "All candidates were duplicates or filtered. Try clearing the library or raising caps.",
          variant: "destructive",
        });
      } else {
        toast({
          title: `${totalImported} real images imported`,
          description: `Sources: Unsplash, Pexels, Pixabay · ${totalSkipped} duplicates skipped`,
        });
      }
      onComplete();
    } catch (err: any) {
      toast({ title: "Seeding error", description: err.message, variant: "destructive" });
    } finally {
      setSeeding(false);
      setStatusText("");
      setProgress(0);
    }
  };

  const isBusy = seeding || clearing || purging;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              className="text-xs border-dashed border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              {clearing ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1" />
              )}
              Vider la bibliothèque
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Vider toute la médiathèque ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action supprimera définitivement tous les fichiers média.
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={clearLibrary}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Tout supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          variant="outline"
          size="sm"
          onClick={purgebrokenRecords}
          disabled={isBusy}
          className="text-xs border-dashed"
        >
          {purging ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <ShieldCheck className="h-3.5 w-3.5 mr-1" />
          )}
          {purging ? "Purge en cours…" : "Purger les liens cassés"}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={seed}
          disabled={isBusy}
          className="text-xs border-dashed border-orange-500/50 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/20"
        >
          {seeding ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <DatabaseZap className="h-3.5 w-3.5 mr-1" />
          )}
          {seeding ? "Importation…" : "Admin Seed (real images)"}
        </Button>
      </div>

      {isBusy && (
        <div className="flex items-center gap-2">
          <Progress value={progress} className="flex-1 h-2" />
          <span className="text-xs text-muted-foreground whitespace-nowrap min-w-[260px]">
            {statusText || `${progress}%`}
          </span>
        </div>
      )}
    </div>
  );
}
